import { useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { StateContext } from "../context/globalContext/context";
import { iGlobalContext } from "../context/globalContext/interface";

export const POST_LOGIN_REDIRECT_KEY = "_post_login_redirect_path";

// Persists the current path when an unauthenticated user is bounced to
// /onboarding. Scoped to /shared/<id> (share-link recipients) and /trips/<id>
// (lares alert-resolution SMS deep links) so recipients who haven't signed in
// yet can be sent through the funnel and end up back on the original page
// after login. Extend the prefix list if other public-ish routes need the
// same treatment.
export function savePostLoginRedirect(pathname: string, search: string = "") {
  if (!pathname) return;
  // Shared-cart links live on the existing Home path with a `?sharedCartId=`
  // query (no dedicated route), so allow them by query in addition to the
  // path-prefix flows.
  const hasSharedCartQuery = (search || "").includes("sharedCartId=");
  if (
    !pathname.startsWith("/shared/") &&
    !pathname.startsWith("/shared-cart/") &&
    !pathname.startsWith("/trips/") &&
    !hasSharedCartQuery
  )
    return;
  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, `${pathname}${search}`);
  } catch {
    // sessionStorage unavailable — best-effort, drop silently.
  }
}

export function readPostLoginRedirect(): string | null {
  try {
    return sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  } catch {
    return null;
  }
}

export function clearPostLoginRedirect() {
  try {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch {
    // ignore
  }
}

// Top-level effect: when a previously-bounced user finishes login, navigate
// them back to the saved path. The onboarding completion handlers fire their
// own `navigateWithParams(ROUTES.Home)` after setting userInfo.loggedIn=true,
// so a single navigate-on-login race-loses to the Home redirect. The fix is
// to make the redirect *sticky*: re-fire on every pathname change while the
// saved key is in sessionStorage and only clear once we've actually landed
// on the saved path. Two-render handoff:
//   1. login → loggedIn flips true → effect runs → navigate(saved)
//   2. onboarding handler fires navigate(Home) → pathname becomes "/" → effect
//      re-runs (pathname is in deps), saved still present → navigate(saved)
//   3. router lands on saved path → effect re-runs, currentPath===saved →
//      clear key, stop firing.
export function usePostLoginRedirect() {
  const { userInfo }: iGlobalContext = useContext(StateContext);
  const navigate = useNavigate();
  const location = useLocation();
  const loggedIn = !!userInfo?.loggedIn && !!userInfo?.id;

  useEffect(() => {
    if (!loggedIn) return;
    const saved = readPostLoginRedirect();
    if (!saved) return;
    const currentPath = location.pathname + (location.search || "");
    if (currentPath === saved) {
      // Landed on the target — clean up and stop firing.
      clearPostLoginRedirect();
      return;
    }
    // Don't clear yet: if a downstream navigate() overrides us, the next
    // pathname change re-runs this effect and we retry.
    navigate(saved, { replace: true });
  }, [loggedIn, navigate, location.pathname, location.search]);
}
